'use client'

import { useRef, type ReactNode } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Check, ChevronRight } from 'lucide-react'
import { Button } from '@mdxui/primitives'
import { Badge } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'

export interface FeaturesStickyHighlightsHighlight {
  text: string
}

export interface FeaturesStickyHighlightsFeature {
  icon?: ReactNode
  title: string
  description: string
  link?: {
    text: string
    url: string
  }
}

export interface FeaturesStickyHighlightsProps {
  /** Badge text */
  badge?: string
  /** Section heading */
  heading?: string
  /** Section description */
  description?: string
  /** Highlight items with checkmarks */
  highlights?: FeaturesStickyHighlightsHighlight[]
  /** Feature cards */
  features?: FeaturesStickyHighlightsFeature[]
  className?: string
}

const defaultHighlights: FeaturesStickyHighlightsHighlight[] = [
  { text: 'User-Friendly Interface for Easy Navigation' },
  { text: 'Real-time Collaboration for Smooth Teamwork' },
  { text: 'Powerful Insights for Informed Decisions' },
]

const defaultFeatures: FeaturesStickyHighlightsFeature[] = [
  {
    title: 'Task Management Simplified',
    description:
      'Keep track of tasks, assign priorities, and monitor progress easily to stay organized and on schedule.',
  },
  {
    title: 'Unified Messaging System',
    description:
      'Communicate efficiently with an intuitive messaging system that supports teams of all sizes and locations.',
  },
  {
    title: 'Effortless Data Sync',
    description:
      'Synchronize data across multiple devices and platforms for a smooth workflow experience for all users.',
  },
  {
    title: 'Team Member Management',
    description:
      'Add and manage team members with ease, ensuring smooth onboarding and collaboration processes.',
  },
  {
    title: 'Stay on Track with Deadlines',
    description:
      'Set reminders and deadlines to keep your team aligned and focused on key goals and objectives.',
  },
]

/**
 * FeaturesStickyHighlights - Sticky header with highlights and scrolling feature cards
 *
 * Split layout with heading, description, and checkmark highlights sticky on left (lg screens)
 * and scrolling feature cards with icons on right.
 */
const FeaturesStickyHighlights = ({
  badge,
  heading = 'Empower Your Workflow with Core Features',
  description = 'Maximize your productivity with essential features designed for seamless operations.',
  highlights = defaultHighlights,
  features = defaultFeatures,
  className,
}: FeaturesStickyHighlightsProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 sm:py-24 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        <div className="relative grid gap-10 lg:grid-cols-2">
          {/* Left side - sticky on lg screens */}
          <motion.div
            className="top-32 mx-auto flex h-fit max-w-3xl flex-col items-center gap-4 lg:sticky lg:items-start"
            variants={headerAnimation}
          >
            {badge && (
              <Badge
                variant="outline"
                className="px-2.5 py-1.5 text-sm"
              >
                {badge}
              </Badge>
            )}
            <h2 className="text-center text-3xl font-semibold lg:text-left lg:text-4xl">
              {heading}
            </h2>
            <div className="text-center lg:text-left">
              <Markdown size="base">{description}</Markdown>
            </div>
            <div className="mt-6 flex flex-col gap-4">
              {highlights.map((highlight) => (
                <div
                  key={highlight.text}
                  className="flex items-center gap-2"
                >
                  <Check className="size-4 text-primary" />
                  <span className="text-muted-foreground">
                    {highlight.text}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right side - scrolling cards */}
          <motion.div
            className="flex flex-col gap-4"
            variants={gridContainerVariants(0.1, 0.2)}
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                className="flex flex-col gap-3 rounded-[var(--radius)] border border-border p-7"
                variants={itemAnimation}
              >
                <div className="flex flex-col items-center gap-2.5 lg:flex-row">
                  {feature.icon && (
                    <span className="flex size-10 items-center justify-center rounded-[var(--radius-md,calc(var(--radius)*0.75))] bg-muted">
                      {feature.icon}
                    </span>
                  )}
                  <h3 className="text-center text-lg font-semibold md:text-left">{feature.title}</h3>
                </div>
                <p className="text-center text-sm text-muted-foreground md:text-base lg:text-left">
                  {feature.description}
                </p>
                {feature.link && (
                  <Button
                    variant="ghost"
                    asChild
                    className="mt-2 w-full sm:w-fit self-center lg:self-start"
                  >
                    <a href={feature.link.url}>
                      {feature.link.text}
                      <ChevronRight className="ml-1 size-4" />
                    </a>
                  </Button>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export { FeaturesStickyHighlights }
